import React, { useState, useEffect } from "react";
import { getAllSubjects } from "./apiClient";

interface Question {
  id: number;
  question_id: number;
  question_img_url: string;
  has_multiple_answer: number;
  duration: number;
  option_count: number;
  exam_id: number;
  subject_id: number;
  chapter_id: number;
  topic_id: number;
  level: string;
  correct_option: string;
  solution: string;
  option_1?: string;
  option_2?: string;
  option_3?: string;
  option_4?: string;
  option_5?: string;
}

interface Subject {
  id: string;
  subject: string;
}

interface QuestionNavigatorSidebarProps {
  filteredQuestions: number[];
  questions: Question[]; // Add questions array
  questionStates: any[];
  currentQuestionIndex: number;
  isQuizActive: boolean;
  isSavingAnswer: boolean;
  activeFilters: Set<string>;
  extensionsUsed: number;
  toggleFilter: (filterType: string) => void;
  setCurrentQuestionIndex: (index: number) => void;
  questionsLength: number;
}

const QuestionNavigatorSidebar: React.FC<QuestionNavigatorSidebarProps> = ({
  filteredQuestions,
  questions,
  questionStates,
  currentQuestionIndex,
  isQuizActive,
  isSavingAnswer,
  activeFilters,
  extensionsUsed,
  toggleFilter,
  setCurrentQuestionIndex,
  questionsLength,
}) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch subjects on component mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await getAllSubjects();
        if (response && response.status === 'success' && response.result) {
          setSubjects(response.result);
          //console.log('Fetched subjects:', response.result);
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  // Group questions by subject_id
  const getQuestionsBySubject = () => {
    const subjectGroups: { [subjectId: number]: number[] } = {};
    
    filteredQuestions.forEach(questionIndex => {
      const question = questions[questionIndex];
      if (question && question.subject_id) {
        if (!subjectGroups[question.subject_id]) {
          subjectGroups[question.subject_id] = [];
        }
        subjectGroups[question.subject_id].push(questionIndex);
      }
    });

    return subjectGroups;
  };

  // Get subject name by id
  const getSubjectName = (subjectId: number): string => {
    const subject = subjects.find(s => parseInt(s.id) === subjectId);
    //console.log('Subject lookup:', subjectId, subject);

    return subject ? subject.subject : `Subject ${subjectId}`;
  };

  // Calculate statistics for filters display
  const getFilterStats = () => {
    const totalCompleted = questionStates.filter(state => state?.selectedOption !== null).length;
    const totalSkipped = questionStates.filter(state => state?.skipped).length;
    const totalViewed = questionStates.filter(state => state?.visited && !state?.selectedOption && !state?.skipped).length;
    
    return {
      completed: totalCompleted,
      skipped: totalSkipped,
      viewed: totalViewed,
      total: questionsLength
    };
  };

  const questionsBySubject = getQuestionsBySubject();
  const filterStats = getFilterStats();

  return (
  <div className="border-start h-100" style={{ backgroundColor: '#ffffff' }}>
    <div className="p-2 h-100" style={{ overflowY: 'auto' }}>
      <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
        <div className="card-body p-3">
          {/* Question Grid - Grouped by Subject */}
          <div className="mb-3">
            {loading ? (
              <div className="text-center p-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading subjects...</span>
                </div>
                <div className="small text-muted mt-2">Loading subjects...</div>
              </div>
            ) : Object.keys(questionsBySubject).length === 0 ? (
              <div className="text-center p-3 text-muted">
                <i className="fas fa-filter mb-2"></i>
                <div className="small">No questions match the current filter</div>
              </div>
            ) : (
              Object.keys(questionsBySubject).map((subjectIdStr) => {
                const subjectId = parseInt(subjectIdStr);
                const subjectQuestions = questionsBySubject[subjectId];
                const subjectName = getSubjectName(subjectId);
                
                return (
                  <div key={subjectId} className="mb-3">
                    {/* Subject Header */}
                    <div className="d-flex align-items-center mb-2">
                      <div className="bg-info text-white px-2 py-1 rounded-pill small fw-bold">
                        {subjectName}
                      </div>
                      <div className="ms-2 text-muted small">
                        ({subjectQuestions.length} questions)
                      </div>
                    </div>
                    
                    {/* Subject Questions */}
                    <div className="row g-1 mb-2">
                      {subjectQuestions.map((index) => {
                        const state = questionStates[index];
                        const isCurrentQuestion = index === currentQuestionIndex;
                        const isAnswered = state?.selectedOption !== null;
                        const isSkipped = state?.skipped;
                        const isVisited = state?.visited;

                        let buttonClass = 'btn btn-outline-secondary';
                        let icon = 'fas fa-question';

                        if (isCurrentQuestion) {
                          buttonClass = 'btn btn-primary';
                          icon = 'fas fa-pen';
                        } else if (isAnswered) {
                          buttonClass = 'btn btn-info';
                          icon = 'fas fa-edit';
                        } else if (isSkipped) {
                          buttonClass = 'btn btn-warning';
                          icon = 'fas fa-forward';
                        } else if (isVisited) {
                          buttonClass = 'btn btn-outline-secondary';
                          icon = 'fas fa-eye';
                        }

                        return (
                          <div key={index} className="col-4 col-sm-3 col-md-2">
                            <button
                              className={`${buttonClass} w-100 p-1`}
                              style={{ borderRadius: '6px', fontSize: '10px', minHeight: '40px', opacity: isSavingAnswer ? 0.6 : 1 }}
                              onClick={() => setCurrentQuestionIndex(index)}
                              disabled={!isQuizActive || isSavingAnswer}
                            >
                              <i className={`${icon} small d-block`}></i>
                              <div className="small mt-1">{index + 1}</div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Question Filters */}
          <hr className="my-2" />
          <div className="mb-2">
            <div className="row g-1">
              {/* All Questions Filter */}
              <div className="col-6">
                <button
                  className={`btn w-100 ${activeFilters.has('all') ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={{ borderRadius: '6px', fontSize: '10px', padding: '6px' }}
                  onClick={() => toggleFilter('all')}
                >
                  <div className="fw-bold">{filterStats.total}</div>
                  <div className="small">All</div>
                </button>
              </div>

              {/* Completed Filter */}
              <div className="col-6">
                <button
                  className={`btn w-100 ${activeFilters.has('completed') ? 'btn-info' : 'btn-outline-info'}`}
                  style={{ borderRadius: '6px', fontSize: '10px', padding: '6px' }}
                  onClick={() => toggleFilter('completed')}
                >
                  <div className="fw-bold">{filterStats.completed}</div>
                  <div className="small">Completed</div>
                </button>
              </div>

              {/* Skipped Filter */}
              <div className="col-6">
                <button
                  className={`btn w-100 ${activeFilters.has('skipped') ? 'btn-warning' : 'btn-outline-warning'}`}
                  style={{ borderRadius: '6px', fontSize: '10px', padding: '6px' }}
                  onClick={() => toggleFilter('skipped')}
                >
                  <div className="fw-bold">{filterStats.skipped}</div>
                  <div className="small">Skipped</div>
                </button>
              </div>

              {/* Viewed Filter */}
              <div className="col-6">
                <button
                  className={`btn w-100 ${activeFilters.has('viewed') ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  style={{ borderRadius: '6px', fontSize: '10px', padding: '6px' }}
                  onClick={() => toggleFilter('viewed')}
                >
                  <div className="fw-bold">{filterStats.viewed}</div>
                  <div className="small">Viewed</div>
                </button>
              </div>
            </div>

            {/* Extensions Info */}
            <div className="text-center mt-2 p-2 bg-light rounded">
              <div className="text-primary fw-bold fs-6">
                {2 - extensionsUsed}
              </div>
              <div className="small text-muted">Time Extensions Left</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default QuestionNavigatorSidebar;
